import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { Card, CardContent, Typography, useMediaQuery, useTheme, Box } from '@mui/material';

const DonutChart = ({ title, labels, value }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [chartOptions, setChartOptions] = useState({
    chart: {
      type: 'donut',
      background: 'transparent',
      fontFamily: theme.typography.fontFamily,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      },
      dropShadow: {
        enabled: true,
        top: 8,
        left: 0,
        blur: 10,
        opacity: 0.15,
        color: '#000'
      }
    },
    labels: labels,
    colors: [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
      '#8884d8',
      '#82ca9d',
      '#ffc658',
      '#ff7300'
    ],
    plotOptions: {
      pie: {
        customScale: 0.95,
        donut: {
          size: '70%',
          background: 'transparent',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              fontWeight: '600',
              color: theme.palette.text.primary,
              offsetY: -5
            },
            value: {
              show: true,
              fontSize: '12px',
              fontWeight: '500',
              color: theme.palette.text.secondary,
              formatter: (val) => `${parseFloat(val).toFixed(1)}%`,
              offsetY: 8
            },
            total: {
              show: true,
              showAlways: true,
              label: 'Total',
              fontSize: '12px',
              fontWeight: '500',
              color: theme.palette.text.secondary,
              formatter: function (w) {
                return '100%';
              }
            }
          }
        },
        expandOnClick: false,
        offsetX: 0,
        offsetY: 0
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val > 5 ? `${val.toFixed(1)}%` : '';
      },
      style: {
        fontSize: '11px',
        fontWeight: '600',
        colors: ['#fff']
      },
      dropShadow: {
        enabled: true,
        top: 2,
        left: 2,
        blur: 3,
        opacity: 0.9,
        color: '#000'
      }
    },
    legend: {
      position: isMobile ? 'bottom' : 'right',
      offsetY: isMobile ? 10 : 0,
      offsetX: isMobile ? 0 : -10,
      fontSize: '12px',
      fontWeight: '500',
      labels: {
        colors: theme.palette.text.primary,
        useSeriesColors: false
      },
      markers: {
        width: 10,
        height: 10,
        radius: 3,
        strokeWidth: 2,
        strokeColor: '#fff'
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4
      }
    },
    stroke: {
      width: 3,
      colors: [theme.palette.background.paper],
      lineCap: 'round'
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'radial',
        shadeIntensity: 0.4,
        gradientToColors: undefined,
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 0.8,
        stops: [0, 100]
      }
    },
    tooltip: {
      theme: theme.palette.mode,
      style: {
        fontSize: '12px'
      },
      y: {
        formatter: function (val) {
          return `${val.toFixed(1)}%`;
        }
      },
      fillSeriesColor: false,
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const color = w.globals.colors[seriesIndex];
        const label = w.globals.labels[seriesIndex];
        const value = series[seriesIndex];
        return `
          <div style="
            background: linear-gradient(145deg, ${color}15, ${color}25);
            border: 1px solid ${color}40;
            border-radius: 8px;
            padding: 8px 12px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          ">
            <div style="
              color: ${theme.palette.text.primary};
              font-weight: 600;
              font-size: 12px;
              margin-bottom: 2px;
            ">${label}</div>
            <div style="
              color: ${color};
              font-weight: 700;
              font-size: 14px;
            ">${value.toFixed(1)}%</div>
          </div>
        `;
      }
    },
    responsive: [
      {
        breakpoint: 600,
        options: {
          chart: {
            width: '100%'
          },
          legend: {
            position: 'bottom'
          }
        }
      }
    ]
  });

  const [chartSeries, setChartSeries] = useState([]);

  useEffect(() => {
    if (value.every(val => val === 0)) {
      setChartSeries([0, 100]);
    } else {
      setChartSeries(value);
    }
  }, [value]);

  useEffect(() => {
    setChartOptions(prev => ({
      ...prev,
      labels: labels,
      colors: [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main,
        '#8884d8',
        '#82ca9d',
        '#ffc658',
        '#ff7300'
      ]
    }));
  }, [labels, theme]);

  return (
    <Card 
      sx={{ 
        width: '100%',
        height: '100%',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #f0f0f0',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)'
        },
        overflow: 'hidden'
      }}
    >
      <CardContent 
        sx={{ 
          p: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'text.primary',
            fontWeight: 600,
            mb: 2,
            textAlign: 'center',
            lineHeight: 1.3
          }}
        >
          {title}
        </Typography>
        
        <Box 
          sx={{ 
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 0
          }}
        >
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="donut"
            width={isMobile ? "100%" : "320"}
            height={isMobile ? "250" : "280"}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default DonutChart;
